'use client';

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary component to catch rendering errors
 * Prevents entire app from crashing when a component throws
 *
 * Usage:
 * <ErrorBoundary>
 *   <ComponentThatMightError />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // After a deploy the browser may hold stale chunks that reference modules
    // which no longer exist at those URLs.  Reload once to pick up the new
    // bundle; guard with sessionStorage so we never loop.
    if (error.name === 'ChunkLoadError') {
      const key = 'chunk-reload-attempted';
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        window.location.reload();
        return;
      }
      sessionStorage.removeItem(key);
    }

    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
    this.setState({ error, errorInfo });
  }

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });

    // Call optional reset callback
    this.props.onReset?.();
  };

  public render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-[400px] flex items-center justify-center p-8 bg-background">
          <div className="max-w-md w-full bg-card border border-border dark:border-border-secondary rounded-lg p-6 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-danger" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-foreground dark:text-text-primary mb-2">
                  Something went wrong
                </h3>
                <p className="text-sm text-muted-foreground dark:text-text-secondary mb-4">
                  An error occurred while rendering this component. Please try refreshing the page.
                </p>

                {/* Show error message in development */}
                {process.env.NODE_ENV === 'development' && this.state.error && (
                  <div className="mb-4 p-3 bg-danger/10 border border-danger rounded-md">
                    <p className="text-xs font-mono text-danger break-all">
                      {this.state.error.message}
                    </p>
                  </div>
                )}

                <button
                  onClick={this.handleReset}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md font-medium hover:bg-primary/90 transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}